// Share your experience (FR-71): a short story for the home page. An
// administrator reviews it first; contact details and links are refused.
import { Button, Paper, Stack, Text, TextInput, Textarea, Title } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { FormError, Notice } from "../../shared/ui/Page";

const LINK = /(https?:\/\/|www\.|\b[a-z0-9-]+\.(com|net|org|io|co|ke|info|biz|app)\b)/i;
const EMAIL = /\S+@\S+\.\S+/;
const PHONE = /(\+?\d[\s-]?){7,}/;

/** hasContact says whether the text carries a link, an email address or a phone number. */
export function hasContact(text: string): boolean {
  return LINK.test(text) || EMAIL.test(text) || PHONE.test(text);
}

export function TestimonialSection() {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setNotice(null);
    const n = name.trim();
    const b = body.trim();
    if (n.length < 1 || n.length > 40) return setError(t("testimonial.bad_name"));
    if (b.length < 20 || b.length > 400) return setError(t("testimonial.bad_length"));
    if (hasContact(n) || hasContact(b)) return setError(t("testimonial.no_contact"));
    setError(null);
    setBusy(true);
    const { data, error, response } = await api.POST("/testimonials", {
      body: { display_name: n, body: b, locale: i18n.language === "sw" ? "sw" : "en" },
    });
    setBusy(false);
    if (response.status === 429) return setError(t("testimonial.too_many"));
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setName("");
    setBody("");
    setNotice(t("testimonial.sent"));
  };

  return (
    <Paper withBorder p="md" radius="md" component="section" aria-labelledby="testimonial-title">
      <form onSubmit={submit} data-testid="testimonial-form" noValidate>
        <Stack gap="sm">
          <Title order={2} size="h4" id="testimonial-title">
            {t("testimonial.title")}
          </Title>
          <Text size="sm">{t("testimonial.intro")}</Text>
          <TextInput
            label={t("testimonial.name")}
            description={t("testimonial.name_hint")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={40}
            required
            autoComplete="given-name"
          />
          <Textarea
            label={t("testimonial.body")}
            description={t("testimonial.body_hint")}
            value={body}
            onChange={(e) => setBody(e.currentTarget.value)}
            maxLength={400}
            minRows={3}
            autosize
            required
          />
          <Text size="xs" c="dimmed" aria-live="polite">
            {t("testimonial.count", { count: body.trim().length })}
          </Text>
          {notice && <Notice>{notice}</Notice>}
          <FormError message={error} />
          <Button type="submit" variant="light" w="fit-content" loading={busy}>
            {t("testimonial.submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
